import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

const nowMarkerPlugin = {
  id: 'nowMarker',
  afterDatasetsDraw(chart, _args, options) {
    if (!options?.enabled) return;

    const xScale = chart.scales.x;
    const chartArea = chart.chartArea;
    if (!xScale || !chartArea) return;

    const now = Date.now();
    const pixelX = xScale.getPixelForValue(now);

    if (pixelX < chartArea.left || pixelX > chartArea.right) return;

    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.65)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(pixelX, chartArea.top);
    ctx.lineTo(pixelX, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }
};

ChartJS.register(
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  nowMarkerPlugin
);

const API_BASE = process.env.REACT_APP_GOIP_STATS_API_BASE || 'https://frek.cc/proxy/38.54.86.24:5007';
const POLL_INTERVAL_MS = 15000;
const MAX_CHART_POINTS = 300;
const TOP_N_OPTIONS = ['all', 5, 10, 20];

const colorPalette = [
  '#90caf9', '#f48fb1', '#4caf50', '#ffb74d', '#ef5350', '#26c6da',
  '#ce93d8', '#ff8a65', '#a5d6a7', '#64b5f6', '#ffd54f', '#81c784'
];

function currentDateStr() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function previousDateStr() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDateStr(dateStr, deltaDays) {
  const match = String(dateStr || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateStr;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  d.setDate(d.getDate() + deltaDays);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createDefaultFilters() {
  const prevDate = previousDateStr();
  return {
    timeFilter: 'minute',
    timeRange: 'last30mins',
    startDate: prevDate,
    endDate: prevDate,
    startTime: '00:00',
    endTime: '23:59',
    topN: 5
  };
}

function getHistoryCacheKey(startDate, endDate) {
  return `${startDate || ''}__${endDate || ''}`;
}

function currentMonthStr() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function statusChipColor(status) {
  if (status === 'good') return 'success';
  if (status === 'bad') return 'error';
  return 'warning';
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatBucket12Hour(bucket) {
  if (!bucket || !bucket.includes(':')) return bucket || '-';
  const [h, m] = bucket.split(':');
  const hh = Number(h);
  const mm = Number(m);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return bucket;
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0b0b10',
      paper: '#15161b'
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(255,255,255,0.65)'
    },
    primary: {
      main: '#90caf9'
    },
    secondary: {
      main: '#f48fb1'
    },
    success: {
      main: '#4caf50'
    },
    warning: {
      main: '#ffb74d'
    },
    error: {
      main: '#f44336'
    }
  }
});

function parseTimestamp(ts) {
  if (!ts) return new Date(NaN);
  const hasTimezone = /([zZ]|[+-]\d\d:?\d\d)$/.test(ts);
  if (hasTimezone) return new Date(ts);

  const normalized = String(ts).trim().replace(' ', 'T');
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2})(?::(\d{2}))?(?::(\d{2}))?(?:\.(\d{1,3}))?$/
  );

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const second = Number(match[6] || 0);
    const ms = Number((match[7] || '0').padEnd(3, '0'));
    return new Date(year, month, day, hour, minute, second, ms);
  }

  return new Date(normalized);
}

function parseDateTimeLocal(date, time, fallbackDate) {
  const targetDate = date || fallbackDate;
  if (!targetDate) return null;

  const dateMatch = String(targetDate).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(time || '00:00').match(/^(\d{2}):(\d{2})$/);
  if (!dateMatch || !timeMatch) return new Date(NaN);

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]) - 1;
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  return new Date(year, month, day, hour, minute, 0, 0);
}

function getTimeRangeBoundary(range, nowMs) {
  if (range === 'last15mins') return nowMs - 15 * 60 * 1000;
  if (range === 'last30mins') return nowMs - 30 * 60 * 1000;
  if (range === 'lastHour') return nowMs - 60 * 60 * 1000;
  if (range === 'last2Hours') return nowMs - 2 * 60 * 60 * 1000;
  return null;
}

function getBucketMs(timestampMs, unit) {
  if (unit === 'hour') return Math.floor(timestampMs / 3600000) * 3600000;
  if (unit === 'minute') return Math.floor(timestampMs / 60000) * 60000;
  return Math.floor(timestampMs / 1000) * 1000;
}

function aggregateData(dataObj, unit, minMs, maxMs) {
  const grouped = new Map();

  Object.entries(dataObj || {}).forEach(([ts, goipValues]) => {
    const date = parseTimestamp(ts);
    if (Number.isNaN(date.getTime())) return;
    const currentMs = date.getTime();

    if (minMs !== null && currentMs < minMs) return;
    if (maxMs !== null && currentMs > maxMs) return;

    const bucketMs = getBucketMs(currentMs, unit);
    if (!grouped.has(bucketMs)) grouped.set(bucketMs, {});

    const bucket = grouped.get(bucketMs);
    Object.entries(goipValues || {}).forEach(([goipKey, value]) => {
      if (!bucket[goipKey]) {
        bucket[goipKey] = { sum: 0, count: 0 };
      }
      bucket[goipKey].sum += Number(value) || 0;
      bucket[goipKey].count += 1;
    });
  });

  const labels = Array.from(grouped.keys()).sort((a, b) => a - b);
  const allGoips = new Set();
  const pointMap = {};

  labels.forEach((bucketMs) => {
    pointMap[bucketMs] = {};
    const bucket = grouped.get(bucketMs);
    Object.keys(bucket).forEach((goipKey) => {
      allGoips.add(goipKey);
      pointMap[bucketMs][goipKey] = bucket[goipKey].sum / bucket[goipKey].count;
    });
  });

  return { labels, allGoips: Array.from(allGoips).sort(), pointMap };
}

function downsampleChart(labels, datasets, maxPoints) {
  if (labels.length <= maxPoints) return { labels, datasets };

  const step = Math.ceil(labels.length / maxPoints);
  const sampledIndex = [];
  for (let i = 0; i < labels.length; i += step) sampledIndex.push(i);
  if (sampledIndex[sampledIndex.length - 1] !== labels.length - 1) sampledIndex.push(labels.length - 1);

  return {
    labels: sampledIndex.map((idx) => labels[idx]),
    datasets: datasets.map((set) => ({
      ...set,
      data: sampledIndex.map((idx) => set.data[idx])
    }))
  };
}

function buildLineDatasets(labels, pointMap, goipKeys) {
  return goipKeys.map((goipKey, index) => {
    const paletteIndex = index % colorPalette.length;
    const dashPattern = index >= colorPalette.length ? [6, 4] : [];

    return {
      label: goipKey,
      data: labels.map((bucketMs) => pointMap[bucketMs][goipKey] || 0),
      borderColor: colorPalette[paletteIndex],
      borderDash: dashPattern,
      borderWidth: 1.8,
      pointRadius: 0,
      fill: false,
      tension: 0.25
    };
  });
}

const GoipStats = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const chartRef = useRef(null);
  const historyCacheRef = useRef(new Map());

  const [tabValue, setTabValue] = useState(0);
  const [rawTodayData, setRawTodayData] = useState({});
  const [rawHistoryData, setRawHistoryData] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);

  const [isLoadingToday, setIsLoadingToday] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [todayError, setTodayError] = useState('');
  const [historyError, setHistoryError] = useState('');

  const [draftFilters, setDraftFilters] = useState(() => createDefaultFilters());
  const [appliedFilters, setAppliedFilters] = useState(() => createDefaultFilters());

  const [selectedGoips, setSelectedGoips] = useState([]);
  const [insightMonth, setInsightMonth] = useState(currentMonthStr());
  const [hourlyInsightDate, setHourlyInsightDate] = useState(previousDateStr());
  const [hourlyInsightBucket, setHourlyInsightBucket] = useState('hour');

  const [dailyInsights, setDailyInsights] = useState({ days: [], summary: null });
  const [hourlyInsights, setHourlyInsights] = useState({ buckets: [], summary: null });
  const [isLoadingDailyInsights, setIsLoadingDailyInsights] = useState(false);
  const [isLoadingHourlyInsights, setIsLoadingHourlyInsights] = useState(false);
  const [dailyInsightsError, setDailyInsightsError] = useState('');
  const [hourlyInsightsError, setHourlyInsightsError] = useState('');

  const todayDataDate = useMemo(() => {
    const dates = Object.keys(rawTodayData || {})
      .map((key) => String(key).slice(0, 10))
      .filter((datePart) => /^\d{4}-\d{2}-\d{2}$/.test(datePart))
      .sort();

    if (!dates.length) return currentDateStr();
    return dates[dates.length - 1];
  }, [rawTodayData]);

  const fetchTodayData = useCallback(async () => {
    try {
      setIsLoadingToday(true);
      const response = await axios.get(`${API_BASE}/today`);
      setRawTodayData(response.data || {});
      setTodayError('');
      setLastUpdated(new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }));
    } catch (_error) {
      setTodayError("Failed to load today's data.");
    } finally {
      setIsLoadingToday(false);
    }
  }, []);

  const fetchHistoryData = useCallback(async (startDate, endDate, { force = false } = {}) => {
    const cacheKey = getHistoryCacheKey(startDate, endDate);
    if (!force && historyCacheRef.current.has(cacheKey)) {
      setRawHistoryData(historyCacheRef.current.get(cacheKey));
      setHistoryError('');
      return;
    }

    try {
      setIsLoadingHistory(true);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(`${API_BASE}/history`, { params });
      const merged = (response.data || []).reduce((acc, entry) => ({
        ...acc,
        ...(entry.data || {})
      }), {});

      historyCacheRef.current.set(cacheKey, merged);
      setRawHistoryData(merged);
      setHistoryError('');
    } catch (_error) {
      setHistoryError('Failed to load historical data.');
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  const fetchDailyInsights = useCallback(async (month) => {
    try {
      setIsLoadingDailyInsights(true);
      const response = await axios.get(`${API_BASE}/insights/daily`, {
        params: { month }
      });
      setDailyInsights(response.data || { days: [], summary: null });
      setDailyInsightsError('');
    } catch (_error) {
      setDailyInsightsError('Failed to load daily insights.');
    } finally {
      setIsLoadingDailyInsights(false);
    }
  }, []);

  const fetchHourlyInsights = useCallback(async ({ date, bucket, source }) => {
    try {
      setIsLoadingHourlyInsights(true);
      const response = await axios.get(`${API_BASE}/insights/hourly`, {
        params: { date, bucket, source }
      });
      setHourlyInsights(response.data || { buckets: [], summary: null });
      setHourlyInsightsError('');
    } catch (_error) {
      setHourlyInsightsError('Failed to load hourly insights.');
    } finally {
      setIsLoadingHourlyInsights(false);
    }
  }, []);

  useEffect(() => {
    fetchTodayData();
    const id = setInterval(fetchTodayData, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchTodayData]);

  useEffect(() => {
    if (tabValue !== 1) return;
    fetchHistoryData(appliedFilters.startDate, appliedFilters.endDate);
  }, [tabValue, appliedFilters.startDate, appliedFilters.endDate, fetchHistoryData]);

  useEffect(() => {
    setAppliedFilters(draftFilters);
  }, [draftFilters]);

  useEffect(() => {
    fetchDailyInsights(insightMonth);
  }, [fetchDailyInsights, insightMonth]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchHourlyInsights({
        date: todayDataDate,
        bucket: hourlyInsightBucket,
        source: 'today'
      });
      return;
    }

    fetchHourlyInsights({
      date: hourlyInsightDate || previousDateStr(),
      bucket: hourlyInsightBucket,
      source: 'history'
    });
  }, [tabValue, hourlyInsightBucket, hourlyInsightDate, fetchHourlyInsights, todayDataDate]);

  const baseTodayChart = useMemo(() => {
    const nowMs = Date.now();
    const rangeMin = getTimeRangeBoundary(appliedFilters.timeRange, nowMs);

    const todayLocal = todayDataDate;
    const start = parseDateTimeLocal('', appliedFilters.startTime, todayLocal);
    const end = parseDateTimeLocal('', appliedFilters.endTime, todayLocal);

    let minMs = rangeMin;
    let maxMs = null;

    if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const startMs = start.getTime();
      const endMs = end.getTime();
      if (startMs <= endMs) {
        minMs = minMs === null ? startMs : Math.max(minMs, startMs);
        maxMs = endMs;
      }
    }

    const { labels, allGoips, pointMap } = aggregateData(
      rawTodayData,
      appliedFilters.timeFilter,
      minMs,
      maxMs
    );

    return {
      labels,
      allGoips,
      lineDatasets: buildLineDatasets(labels, pointMap, allGoips)
    };
  }, [rawTodayData, todayDataDate, appliedFilters.timeFilter, appliedFilters.timeRange, appliedFilters.startTime, appliedFilters.endTime]);

  const baseHistoryChart = useMemo(() => {
    const start = parseDateTimeLocal(appliedFilters.startDate, appliedFilters.startTime, null);
    const end = parseDateTimeLocal(appliedFilters.endDate, appliedFilters.endTime, null);

    const minMs = start && !Number.isNaN(start.getTime()) ? start.getTime() : null;
    const maxMs = end && !Number.isNaN(end.getTime()) ? end.getTime() : null;

    const { labels, allGoips, pointMap } = aggregateData(
      rawHistoryData,
      appliedFilters.timeFilter,
      minMs,
      maxMs
    );

    return {
      labels,
      allGoips,
      lineDatasets: buildLineDatasets(labels, pointMap, allGoips)
    };
  }, [rawHistoryData, appliedFilters.startDate, appliedFilters.endDate, appliedFilters.startTime, appliedFilters.endTime, appliedFilters.timeFilter]);

  const activeChart = tabValue === 0 ? baseTodayChart : baseHistoryChart;

  useEffect(() => {
    setSelectedGoips((prev) => {
      if (!activeChart.allGoips.length) return [];
      const previousSet = new Set(prev);
      const stillValid = activeChart.allGoips.filter((key) => previousSet.has(key));
      return stillValid.length ? stillValid : [...activeChart.allGoips];
    });
  }, [activeChart.allGoips]);

  const visibleDatasets = useMemo(() => {
    const selected = new Set(selectedGoips);
    let datasets = activeChart.lineDatasets.filter((set) => selected.has(set.label));

    if (appliedFilters.topN !== 'all') {
      const topCount = Number(appliedFilters.topN);
      datasets = [...datasets]
        .sort((a, b) => {
          const aAvg = a.data.length ? a.data.reduce((sum, val) => sum + val, 0) / a.data.length : 0;
          const bAvg = b.data.length ? b.data.reduce((sum, val) => sum + val, 0) / b.data.length : 0;
          return bAvg - aAvg;
        })
        .slice(0, topCount);
    }

    return datasets;
  }, [activeChart.lineDatasets, selectedGoips, appliedFilters.topN]);

  const goipColorMap = useMemo(() => {
    const map = {};
    activeChart.allGoips.forEach((goipKey, index) => {
      map[goipKey] = colorPalette[index % colorPalette.length];
    });
    return map;
  }, [activeChart.allGoips]);

  const chartData = useMemo(() => {
    const sampled = downsampleChart(activeChart.labels, visibleDatasets, MAX_CHART_POINTS);

    return {
      labels: sampled.labels.map((ms) => new Date(ms)),
      datasets: sampled.datasets
    };
  }, [activeChart.labels, visibleDatasets]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          footer(items) {
            const total = items.reduce((sum, item) => sum + (Number(item.raw) || 0), 0);
            return `Total: ${total.toFixed(2)}`;
          }
        }
      },
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          drag: { enabled: true },
          mode: 'x'
        }
      },
      nowMarker: {
        enabled: tabValue === 0
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: appliedFilters.timeFilter === 'hour'
            ? 'hour'
            : appliedFilters.timeFilter === 'minute'
            ? 'minute'
            : 'second',
          displayFormats: {
            hour: 'h:mm a',
            minute: 'h:mm a',
            second: 'h:mm:ss a'
          }
        },
        ticks: {
          color: '#e8e9ee',
          autoSkip: true,
          maxTicksLimit: isMobile ? 5 : 10
        },
        grid: {
          color: 'rgba(255,255,255,0.1)'
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#e8e9ee'
        },
        grid: {
          color: 'rgba(255,255,255,0.1)'
        }
      }
    }
  }), [appliedFilters.timeFilter, isMobile, tabValue]);

  const handleResetFilters = () => {
    setDraftFilters(createDefaultFilters());
  };

  const shiftHistoricalDays = (deltaDays) => {
    setDraftFilters((prev) => {
      if (!prev.startDate && !prev.endDate) return prev;
      return {
        ...prev,
        startDate: prev.startDate ? shiftDateStr(prev.startDate, deltaDays) : prev.startDate,
        endDate: prev.endDate ? shiftDateStr(prev.endDate, deltaDays) : prev.endDate
      };
    });
  };

  const toggleGoip = (goipKey) => {
    setSelectedGoips((prev) => {
      if (prev.includes(goipKey)) return prev.filter((key) => key !== goipKey);
      return [...prev, goipKey];
    });
  };

  const selectAllGoips = () => setSelectedGoips([...activeChart.allGoips]);
  const clearGoips = () => setSelectedGoips([]);

  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const isLoading = tabValue === 0 ? isLoadingToday : isLoadingHistory;
  const activeError = tabValue === 0 ? todayError : historyError;
  const hasData = chartData.datasets.length > 0 && chartData.labels.length > 0;

  const quickRangeButtons = [
    { label: '15m', value: 'last15mins' },
    { label: '30m', value: 'last30mins' },
    { label: '1h', value: 'lastHour' },
    { label: '2h', value: 'last2Hours' },
    { label: 'All', value: 'all' }
  ];

  const dailyInsightRows = useMemo(() => dailyInsights.days || [], [dailyInsights.days]);
  const hourlyInsightRows = useMemo(() => hourlyInsights.buckets || [], [hourlyInsights.buckets]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          py: 2,
          background:
            'radial-gradient(circle at 20% 20%, rgba(144,202,249,0.08), transparent 40%), radial-gradient(circle at 80% 0%, rgba(244,143,177,0.08), transparent 35%), #0b0b10'
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)'
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Goip Calls Statistics
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {lastUpdated && (
                    <Typography variant="caption" color="text.secondary">
                      Last Updated: {lastUpdated}
                    </Typography>
                  )}
                  <Button variant="outlined" size="small" onClick={fetchTodayData}>
                    Refresh Today
                  </Button>
                  {tabValue === 1 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => fetchHistoryData(appliedFilters.startDate, appliedFilters.endDate, { force: true })}
                    >
                      Refresh History
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />

              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Today's Data" />
                <Tab label="Historical Data" />
              </Tabs>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)'
              }}
            >
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Time Filter</InputLabel>
                      <Select
                        value={draftFilters.timeFilter}
                        label="Time Filter"
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, timeFilter: e.target.value }))}
                      >
                        <MenuItem value="second">Second</MenuItem>
                        <MenuItem value="minute">Minute</MenuItem>
                        <MenuItem value="hour">Hour</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {tabValue === 0 && (
                    <Grid item xs={12} sm={6} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Time Range</InputLabel>
                        <Select
                          value={draftFilters.timeRange}
                          label="Time Range"
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, timeRange: e.target.value }))}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="last15mins">Last 15 Minutes</MenuItem>
                          <MenuItem value="last30mins">Last 30 Minutes</MenuItem>
                          <MenuItem value="lastHour">Last Hour</MenuItem>
                          <MenuItem value="last2Hours">Last 2 Hours</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="Start Time"
                      type="time"
                      fullWidth
                      size="small"
                      value={draftFilters.startTime}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, startTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      label="End Time"
                      type="time"
                      fullWidth
                      size="small"
                      value={draftFilters.endTime}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, endTime: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {tabValue === 1 && (
                    <>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          label="Start Date"
                          type="date"
                          fullWidth
                          size="small"
                          value={draftFilters.startDate}
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <TextField
                          label="End Date"
                          type="date"
                          fullWidth
                          size="small"
                          value={draftFilters.endDate}
                          onChange={(e) => setDraftFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Top N</InputLabel>
                      <Select
                        value={draftFilters.topN}
                        label="Top N"
                        onChange={(e) => setDraftFilters((prev) => ({ ...prev, topN: e.target.value }))}
                      >
                        {TOP_N_OPTIONS.map((option) => (
                          <MenuItem key={String(option)} value={option}>
                            {option === 'all' ? 'All' : `Top ${option}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {tabValue === 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {quickRangeButtons.map((range) => (
                      <Button
                        key={range.value}
                        size="small"
                        variant={draftFilters.timeRange === range.value ? 'contained' : 'outlined'}
                        onClick={() => setDraftFilters((prev) => ({ ...prev, timeRange: range.value }))}
                      >
                        {range.label}
                      </Button>
                    ))}
                  </Stack>
                )}

                {tabValue === 1 && (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => shiftHistoricalDays(-1)}>
                      {'<- Previous Day'}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => shiftHistoricalDays(1)}>
                      {'Next Day ->'}
                    </Button>
                  </Stack>
                )}

                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={handleResetFilters}>Reset</Button>
                  <Button variant="outlined" onClick={resetZoom}>Reset Zoom</Button>
                </Stack>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={selectAllGoips}>Select All</Button>
                    <Button size="small" variant="outlined" onClick={clearGoips}>Clear</Button>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {activeChart.allGoips.map((goipKey) => {
                      const selected = selectedGoips.includes(goipKey);
                      const lineColor = goipColorMap[goipKey] || '#90caf9';
                      return (
                        <Chip
                          key={goipKey}
                          label={goipKey}
                          size="small"
                          clickable
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => toggleGoip(goipKey)}
                          sx={{
                            borderColor: lineColor,
                            color: selected ? '#0b0b10' : lineColor,
                            backgroundColor: selected ? `${lineColor}CC` : 'transparent',
                            '&:hover': {
                              backgroundColor: selected ? `${lineColor}E6` : `${lineColor}22`
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)'
              }}
            >
              {activeError && <Alert severity="error" sx={{ mb: 1.5 }}>{activeError}</Alert>}

              <Box sx={{ height: { xs: 360, md: 520 }, position: 'relative' }}>
                {isLoading && (
                  <Stack
                    sx={{ position: 'absolute', inset: 0, zIndex: 2, background: 'rgba(0,0,0,0.3)' }}
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="caption">Loading data...</Typography>
                  </Stack>
                )}

                {hasData ? (
                  <Line ref={chartRef} data={chartData} options={chartOptions} />
                ) : (
                  !isLoading && (
                    <Typography align="center" variant="body2" sx={{ mt: 4 }} color="text.secondary">
                      {tabValue === 0 ? 'No data available for today.' : 'No historical data available.'}
                    </Typography>
                  )
                )}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)'
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Hourly Insights ({tabValue === 0 ? 'Today' : 'History'})
                  </Typography>
                  {tabValue === 1 && (
                    <TextField
                      type="date"
                      size="small"
                      value={hourlyInsightDate}
                      onChange={(e) => setHourlyInsightDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Bucket</InputLabel>
                    <Select
                      value={hourlyInsightBucket}
                      label="Bucket"
                      onChange={(e) => setHourlyInsightBucket(e.target.value)}
                    >
                      <MenuItem value="hour">Hourly</MenuItem>
                      <MenuItem value="30min">30 Minutes</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => fetchHourlyInsights({
                      date: tabValue === 0 ? todayDataDate : (hourlyInsightDate || previousDateStr()),
                      bucket: hourlyInsightBucket,
                      source: tabValue === 0 ? 'today' : 'history'
                    })}
                  >
                    Refresh Insights
                  </Button>
                </Stack>

                {hourlyInsightsError && <Alert severity="error">{hourlyInsightsError}</Alert>}

                {hourlyInsights.summary && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Chip label={`Average: ${hourlyInsights.summary.average_bucket_load ?? 0}`} size="small" />
                    <Chip label={`Peak: ${formatBucket12Hour(hourlyInsights.summary.peak_bucket)}`} size="small" />
                    <Chip label={`Lowest: ${formatBucket12Hour(hourlyInsights.summary.lowest_bucket)}`} size="small" />
                  </Stack>
                )}

                <TableContainer component={Paper} sx={{ background: 'rgba(13,14,18,0.8)' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Bucket</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Avg Calls</TableCell>
                        <TableCell align="right">Peak Calls</TableCell>
                        <TableCell>Top GoIP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoadingHourlyInsights ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2">Loading hourly insights...</Typography>
                          </TableCell>
                        </TableRow>
                      ) : hourlyInsightRows.length ? (
                        hourlyInsightRows.map((row) => (
                          <TableRow key={row.bucket}>
                            <TableCell>{formatBucket12Hour(row.bucket)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={statusChipColor(row.status)}
                                label={(row.status || 'normal').toUpperCase()}
                              />
                            </TableCell>
                            <TableCell align="right">{row.average_total_calls ?? 0}</TableCell>
                            <TableCell align="right">{row.peak_total_calls ?? 0}</TableCell>
                            <TableCell>{row.top_goip || '-'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary">
                              No hourly insights available for the selected day.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>

            {tabValue === 1 && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  background: 'rgba(18, 18, 22, 0.75)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(6px)'
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Daily Insights (Monthly)
                    </Typography>
                    <TextField
                      type="month"
                      size="small"
                      value={insightMonth}
                      onChange={(e) => setInsightMonth(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Button size="small" variant="outlined" onClick={() => fetchDailyInsights(insightMonth)}>
                      Refresh Insights
                    </Button>
                  </Stack>

                  {dailyInsightsError && <Alert severity="error">{dailyInsightsError}</Alert>}

                  {dailyInsights.summary && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Chip label={`Avg Load: ${dailyInsights.summary.average_daily_load ?? 0}`} size="small" />
                      <Chip label={`Best Day: ${formatDateDisplay(dailyInsights.summary.best_day)}`} size="small" />
                      <Chip label={`Worst Day: ${formatDateDisplay(dailyInsights.summary.worst_day)}`} size="small" />
                    </Stack>
                  )}

                  <TableContainer component={Paper} sx={{ background: 'rgba(13,14,18,0.8)' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Avg Calls</TableCell>
                          <TableCell align="right">Peak Calls</TableCell>
                          <TableCell>Top GoIP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoadingDailyInsights ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2">Loading daily insights...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : dailyInsightRows.length ? (
                          dailyInsightRows.map((row) => (
                            <TableRow key={row.date}>
                              <TableCell>{formatDateDisplay(row.date)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={statusChipColor(row.status)}
                                  label={(row.status || 'normal').toUpperCase()}
                                />
                              </TableCell>
                              <TableCell align="right">{row.average_total_calls ?? 0}</TableCell>
                              <TableCell align="right">{row.peak_total_calls ?? 0}</TableCell>
                              <TableCell>{row.top_goip || '-'}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary">
                                No daily insights available for this month.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default GoipStats;
